<script setup lang="ts">
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { useDefaultStore } from "@/store";
import { useToast } from "primevue/usetoast";

definePage({
	name: "MusicSettingsPage",
});

const store = useDefaultStore();
const toast = useToast();
const router = useRouter();

const username = ref("");
const initialUsername = ref("");
const isLoading = ref(true);
const isSaving = ref(false);

const hasChanges = computed(() => {
	return username.value.trim() !== initialUsername.value.trim();
});

async function loadUser() {
	try {
		isLoading.value = true;

		if (!store.userData?.usr_token) {
			await store.getUser();
		}

		const currentUsername = store.userData?.usr_username ?? "";

		username.value = currentUsername;
		initialUsername.value = currentUsername;
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to load user settings.",
			life: 3000,
		});
	} finally {
		isLoading.value = false;
	}
}

async function saveUsername() {
	const trimmedName = username.value.trim();
	const token = store.userData?.usr_token ?? "";

	if (!token) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Missing user token.",
			life: 3000,
		});
		return;
	}

	if (!trimmedName) {
		toast.add({
			severity: "warn",
			summary: "Validation",
			detail: "Username cannot be empty.",
			life: 2500,
		});
		return;
	}

	if (!hasChanges.value) {
		return;
	}

	try {
		isSaving.value = true;

		const res = await api.editUser({
			token,
			name: trimmedName,
		});

		const data = res.data.data;

		if (!data?.ok) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: data?.showText
					? (data?.message ?? "Unable to save username.")
					: "Unable to save username.",
				life: 3500,
			});

			await router.push({
				name: "MusicPage",
			});
			return;
		}

		await store.getUser();

		const updatedUsername = store.userData?.usr_username ?? trimmedName;

		username.value = updatedUsername;
		initialUsername.value = updatedUsername;

		toast.add({
			severity: "success",
			summary: "Saved",
			detail: "Username updated successfully.",
			life: 2500,
		});
	} catch (err: any) {
		console.warn(err.message);
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText
				? (customErrorMsg ?? "Unable to save username.")
				: "Unable to save username.",
			life: 3500,
		});

		await router.push({
			name: "MusicPage",
		});
	} finally {
		isSaving.value = false;
	}
}

onMounted(() => {
	loadUser();
});

useHead({
	title: getPageTitle("Settings"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Settings"),
		},
		{
			name: "description",
			content: `Manage your ${APP_NAME} account settings.`,
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("Settings"),
		},
		{
			property: "og:description",
			content: `Manage your ${APP_NAME} account settings.`,
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<section class="settings-page">
		<div class="overlay"></div>

		<div class="page-shell">
			<div class="settings-card">
				<RouterLink
					:to="{ name: 'MusicPage' }"
					class="category-back-link"
				>
					← Back
				</RouterLink>

				<div class="header">
					<p class="eyebrow">Settings</p>
					<h1 class="title">Profile Settings</h1>
				</div>

				<div
					v-if="isLoading"
					class="state-box"
				>
					Loading settings...
				</div>

				<div
					v-else
					class="form-block"
				>
					<label
						for="username"
						class="field-label"
					>
						Username
					</label>

					<input
						id="username"
						v-model="username"
						type="text"
						class="field-input"
						maxlength="40"
						placeholder="Enter username"
						:disabled="isSaving"
					/>

					<button
						type="button"
						class="save-button"
						:disabled="isSaving || !hasChanges"
						@click="saveUsername"
					>
						{{ isSaving ? "Saving..." : "Save Changes" }}
					</button>
				</div>
			</div>
		</div>
	</section>
</template>

<!-- <style scoped lang="scss">
.field-help {
	margin: 0;
	font-size: 13px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.62);
}
</style> -->
